/**
 * Hook for loading entity details in EntityDetailDialog
 *
 * This hook handles:
 * 1. Entity fetching with expansion
 * 2. Photo detection and loading
 * 3. Schema attribute loading for Set entities
 * 4. ActionWorkflow validation for SetTransition entities
 * 5. PossessiveTargetAttribute info for RequestApprovalType entities
 * 6. Delete state management
 * 7. Reference click handling for nested entity viewing
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ScimEntityService } from '../services/ScimEntityService';
import { entityService, photoService } from '../services/entityServiceSingleton';
import { ScimSchemaService } from '../services/ScimSchemaService';
import EntitlementsService from '../services/EntitlementsService';
import type { EntityEntitlementForTargetResponse } from '../types/EntityEntitlements';
import { extractErrorMessage } from '../utils/apiUtils';
import {
    getIndexedReadableAttributeDisplayNamesFromTargetResponse,
    getNonIndexedHasLengthCountAttributeDisplayNamesForEagerLoad,
    getLargeArrayRefAttributeDisplayNamesToExcludeFromEagerLoad
} from '../types/EntityEntitlements';
import { EntityCategorizationService } from '../services/EntityCategorizationService';
import CentralizedSchemaService from '../services/CentralizedSchemaService';
import { dedupeAttributeNamesForRequest } from '../utils/entityPropertyUtils';
import { extractReferenceId, extractReferenceDisplayName } from '../utils/referenceUtils';
import { isContentBodyLoadedOrFullScreenHash } from '../utils/hashQueryParams';
import { isEntityType, EntityType } from '../utils/entityTypeDetection';
import type { ScimEntity } from '../types/ScimEntity';

/** Known PascalCase/camelCase pairs that should not appear twice in raw entity (e.g. Raw JSON tab). Keep PascalCase only. */
const PASCAL_CAMEL_PAIRS: [string, string][] = [
    ['Id', 'id'],
    ['Name', 'name'],
    ['DisplayName', 'displayName'],
    ['Type', 'type'],
    ['Required', 'required'],
    ['MultiValued', 'multiValued'],
    ['EntityType', 'entityType']
];

/** Ensure entity uses PascalCase only for known pairs; remove camelCase duplicates so raw JSON and consumers don't see both. */
function normalizeEntityId(entity: any): any {
    if (!entity || typeof entity !== 'object' || Array.isArray(entity)) return entity;
    const out = { ...entity };
    for (const [pascal, camel] of PASCAL_CAMEL_PAIRS) {
        if (camel in out && (pascal in out || (out as any)[camel] !== undefined)) {
            if (!(pascal in out)) (out as any)[pascal] = (out as any)[camel];
            delete (out as any)[camel];
        }
    }
    return out;
}

/** Treat invalid IDs (literal "undefined", empty) as missing so we never fetch with them */
function normalizeId(id: string | null | undefined): string | null {
    if (id == null) return null;
    const s = typeof id === 'string' ? id.trim() : String(id).trim();
    return s !== '' && s !== 'undefined' ? s : null;
}

export interface EntityDetailLoaderOptions {
    open: boolean;
    entity: ScimEntity | null;
    entityType: string;
    selectedEntityId?: string | null;
    /** When true, include Content.Body in the primary entity fetch (for deep links that need Body loaded immediately). */
    includeContentBody?: boolean;
}

export interface PhotoState {
    photoData: string | null;
    photoLoading: boolean;
    shouldShowPhoto: boolean;
    photoExpanded: boolean;
    setPhotoExpanded: (expanded: boolean) => void;
}

export interface SchemaState {
    schemaAttributes: any[];
    schemaLoading: boolean;
}

export interface DeleteState {
    isDeleting: boolean;
    deleteError: string | null;
    deleteDialogOpen: boolean;
    setIsDeleting: (deleting: boolean) => void;
    setDeleteError: (error: string | null) => void;
    setDeleteDialogOpen: (open: boolean) => void;
}

export interface ReferenceState {
    referenceDialogOpen: boolean;
    referenceEntity: any | null;
    referenceEntityType: string;
    referenceLoading: boolean;
    handleReferenceClick: (reference: any) => Promise<void>;
    closeReferenceDialog: () => void;
}

export interface UseEntityDetailLoaderResult {
    // Entity state
    fullEntity: any | null;
    isLoading: boolean;
    error: string | null;

    // Photo state
    photo: PhotoState;

    // Schema state (for Set entities)
    schema: SchemaState;

    // Delete state
    deleteState: DeleteState;

    // Reference state (for clicking on references)
    reference: ReferenceState;

    // ActionWorkflow validation (for SetTransition)
    actionWorkflowError: string | null;
    actionWorkflowValidating: boolean;

    // PossessiveTargetAttribute info (for RequestApprovalType)
    possessiveTargetAttributeInfo: { entities: string[]; isReferenceTo: string[] } | null;

    // Schema bindings for this entity type (Raw Views tab, admins only)
    schemaEntityBindings: any | null;
    schemaEntitySchemaLoading: boolean;

    /** Tier3: entitlement for this specific entity (restricted view). Use for read/edit filtering in detail. */
    targetEntitlement: EntityEntitlementForTargetResponse | null;

    /** When set, the tier3 (per-entity) entitlements request failed; show in view so user sees the warning. */
    targetEntitlementError: string | null;

    /** Load a single non-indexed attribute and merge into fullEntity. Returns the merged entity when successful (e.g. for passing to edit). */
    loadNonIndexedAttribute: (attributeKey: string) => Promise<any | null>;
    loadingAttribute: string | null;

    // Services (exposed for use in component)
    entityService: ScimEntityService;
}

export function useEntityDetailLoader(
    options: EntityDetailLoaderOptions
): UseEntityDetailLoaderResult {
    const { open, entity, entityType, selectedEntityId, includeContentBody } = options;
    const queryClient = useQueryClient();

    // Photo UI state
    const [photoExpanded, setPhotoExpanded] = useState(false);

    // Delete state
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Reference state
    const [referenceDialogOpen, setReferenceDialogOpen] = useState(false);
    const [referenceEntity, setReferenceEntity] = useState<any | null>(null);
    const [referenceEntityType, setReferenceEntityType] = useState<string>('');
    const [referenceLoading, setReferenceLoading] = useState(false);

    // PossessiveTargetAttribute info state
    const [possessiveTargetAttributeInfo, setPossessiveTargetAttributeInfo] = useState<{
        entities: string[];
        isReferenceTo: string[];
    } | null>(null);

    // Loading a single non-indexed attribute (for "Load" link in detail view)
    const [loadingAttribute, setLoadingAttribute] = useState<string | null>(null);

    // Local state for attributes loaded on-demand by loadNonIndexedAttribute
    const [additionalLoadedAttrs, setAdditionalLoadedAttrs] = useState<Record<string, any>>({});

    // Schema bindings for this entity type (Raw Views → Schema & Bindings tab, admins only)
    const [schemaEntityBindings, setSchemaEntityBindings] = useState<any | null>(null);
    const [schemaEntitySchemaLoading, setSchemaEntitySchemaLoading] = useState(false);

    const categorizationService = useMemo(() => EntityCategorizationService.getInstance(), []);

    const entityId = useMemo(() => {
        const idFromUrl = normalizeId(selectedEntityId);
        const idFromEntity = normalizeId(entity?.Id ?? (typeof entity === 'string' ? entity : null));
        return idFromUrl || idFromEntity || null;
    }, [selectedEntityId, entity?.Id]);

    const schemaEntityId = useMemo(() => {
        try {
            const hash = typeof window !== 'undefined' ? (window.location.hash ?? '') : '';
            const [hashWithoutSearch] = hash.replace(/^#/, '').split('?');
            const parts = (hashWithoutSearch ?? '').split('/').filter(Boolean);
            if (parts[0]?.toLowerCase() === 'person' && parts[1]?.trim()) {
                return parts[1].trim().toLowerCase();
            }
        } catch {
            // ignore
        }
        return entityType.trim().toLowerCase();
    }, [entityType]);

    const entitlementQuery = useQuery<EntityEntitlementForTargetResponse | null>({
        queryKey: ['entitlement', 'target', schemaEntityId, entityId],
        queryFn: () => EntitlementsService.fetchEntityEntitlementForTarget(schemaEntityId, entityId!),
        enabled: open && !!entityId,
        retry: 0,
    });

    const requestAttributes = useMemo(() => {
        const entitlement = entitlementQuery.data ?? null;
        const indexedDisplayNames = getIndexedReadableAttributeDisplayNamesFromTargetResponse(entitlement);
        const nonIndexedEagerLoadDisplayNames =
            getNonIndexedHasLengthCountAttributeDisplayNamesForEagerLoad(entitlement);
        const excludeDisplayNames = new Set(
            getLargeArrayRefAttributeDisplayNamesToExcludeFromEagerLoad(entitlement).map((a) =>
                (a ?? '').toString().trim().toLowerCase()
            )
        );
        const baseAttrs = ['Id', 'DisplayName', ...(indexedDisplayNames ?? []), ...(nonIndexedEagerLoadDisplayNames ?? [])]
            .filter((a) => !excludeDisplayNames.has((a ?? '').toString().trim().toLowerCase()));
        let rawAttrs: string[] | undefined = baseAttrs.length > 0 ? [...new Set(baseAttrs)] : undefined;

        if (
            entityType &&
            (isEntityType(entityType, EntityType.Workflow) || isEntityType(entityType, EntityType.WorkflowActivity))
        ) {
            const workflowBase = ['Id', 'DisplayName', 'Settings'];
            const workflowAttrs = isEntityType(entityType, EntityType.Workflow)
                ? [...workflowBase, 'Type']
                : [...workflowBase, 'Description', 'Script', 'Action', 'Authorization', 'EntityType'];
            rawAttrs = rawAttrs ? [...new Set([...rawAttrs, ...workflowAttrs])] : workflowAttrs;
        }

        const contentBodyMode = isContentBodyLoadedOrFullScreenHash();
        const wantsBodyInPrimaryFetch = includeContentBody === true || contentBodyMode;
        const isBodyEntity =
            isEntityType(entityType, EntityType.Content) || isEntityType(entityType, EntityType.EmailTemplate);
        const requestAttributesWithDeepLink =
            wantsBodyInPrimaryFetch && isBodyEntity
                ? [...new Set([...(rawAttrs ?? []), 'Body'])]
                : rawAttrs;

        return requestAttributesWithDeepLink
            ? dedupeAttributeNamesForRequest(requestAttributesWithDeepLink)
            : undefined;
    }, [entitlementQuery.data, entityType, includeContentBody]);

    const entitlementSettled =
        !open || !entityId || entitlementQuery.isSuccess || entitlementQuery.isError;

    const entityQuery = useQuery<ScimEntity>({
        queryKey: ['entity', entityType, entityId, requestAttributes, false] as const,
        queryFn: () => entityService.getEntityById(entityId!, entityType, false, requestAttributes),
        enabled: open && !!entityId && entitlementSettled,
        select: (data) => normalizeEntityId(data),
        retry: 0,
    });

    useEffect(() => {
        setAdditionalLoadedAttrs({});
    }, [entityId]);

    useEffect(() => {
        if (!open || !entityId) {
            setDeleteError(null);
        }
    }, [open, entityId]);

    const mergeEntityWithAdditionalAttrs = useCallback(
        (baseEntity: Record<string, any>): ScimEntity => {
            const merged = { ...baseEntity };
            const additionalKeys = Object.keys(additionalLoadedAttrs);
            if (additionalKeys.length > 0) {
                for (const [key, value] of Object.entries(additionalLoadedAttrs)) {
                    merged[key] = value;
                }
                const prevRequested = Array.isArray(merged._attributesRequested)
                    ? (merged._attributesRequested as string[])
                    : [];
                const lowerRequested = new Set(prevRequested.map((a: string) => (a ?? '').toLowerCase()));
                const uniqueNew = additionalKeys.filter((k) => !lowerRequested.has((k ?? '').toLowerCase()));
                merged._attributesRequested = [...prevRequested, ...uniqueNew];
                merged._attributesMerged = [
                    ...(Array.isArray(merged._attributesMerged) ? merged._attributesMerged : []),
                    ...additionalKeys,
                ];
            }
            return merged as ScimEntity;
        },
        [additionalLoadedAttrs]
    );

    const fullEntity = useMemo((): ScimEntity | null => {
        if (!open || !entityId) return null;

        const queryData = entityQuery.data;
        if (queryData) {
            const base =
                entity && typeof entity === 'object' && !Array.isArray(entity) ? { ...entity } : {};
            return mergeEntityWithAdditionalAttrs({ ...base, ...queryData });
        }

        if (entityQuery.isError && entityQuery.error) {
            const errorMessage =
                entityQuery.error instanceof Error
                    ? entityQuery.error.message
                    : 'Failed to load entity details';
            const isNoPermission = errorMessage.includes('do not have permission to view');
            if (isNoPermission) return null;

            if (entity && typeof entity === 'object' && !Array.isArray(entity)) {
                return mergeEntityWithAdditionalAttrs({ ...entity });
            }
        }

        return null;
    }, [
        open,
        entityId,
        entityQuery.data,
        entityQuery.isError,
        entityQuery.error,
        entity,
        mergeEntityWithAdditionalAttrs,
    ]);

    const isLoading =
        open && !!entityId && (entitlementQuery.isFetching || entityQuery.isFetching);

    const error =
        !open || !entityId
            ? null
            : entityQuery.error
              ? entityQuery.error instanceof Error
                  ? entityQuery.error.message
                  : 'Failed to load entity details'
              : null;

    const targetEntitlement = useMemo(() => {
        if (!open || !entityId) return null;
        if (error?.includes('do not have permission to view')) return null;
        return entitlementQuery.data ?? null;
    }, [open, entityId, error, entitlementQuery.data]);

    const targetEntitlementError = entitlementQuery.error
        ? extractErrorMessage(entitlementQuery.error) || 'Entitlements (view permissions) request failed'
        : null;

    const checkShouldShowPhoto = useCallback((entityToCheck: any, type: string): boolean => {
        if (!entityToCheck) return false;

        if (isEntityType(type, EntityType.Photo) && entityToCheck.Data) {
            return true;
        }

        return !!(
            entityToCheck.Photo &&
            (entityToCheck.Photo.$ref || entityToCheck.Photo.Id || entityToCheck.Photo.Data)
        );
    }, []);

    const shouldShowPhoto = fullEntity ? checkShouldShowPhoto(fullEntity, entityType) : false;
    const photoRef = (fullEntity?.Photo as Record<string, any>)?.$ref;
    const isPhotoEntity = isEntityType(entityType, EntityType.Photo);
    const photoDataFromEntity = isPhotoEntity ? (fullEntity?.Data as string | null) : null;

    const photoQuery = useQuery<string | null>({
        queryKey: ['photo', photoRef],
        queryFn: async () => {
            const response = await photoService.getPhotoFromRef(photoRef!);
            return response.Photo || null;
        },
        enabled: shouldShowPhoto && !!photoRef && !isPhotoEntity,
    });

    const photoData = isPhotoEntity ? photoDataFromEntity : (photoQuery.data ?? null);
    const photoLoading = isPhotoEntity ? false : photoQuery.isFetching;

    const isSetEntity = isEntityType(entityType, EntityType.Set);
    const filterScopeValue = fullEntity?.FilterScope || fullEntity?.filterScope;
    const filterScopeString =
        extractReferenceId(filterScopeValue) || extractReferenceDisplayName(filterScopeValue) || '';

    const schemaQuery = useQuery({
        queryKey: ['setSchema', filterScopeString],
        queryFn: async () => {
            const centralizedService = CentralizedSchemaService.getInstance();
            const attributes = await centralizedService.getFieldOptionsForEntityType(filterScopeString);
            return attributes.map((attr) => ({
                Id: attr.id || attr.name,
                Name: attr.label || attr.name,
                Type: attr.type || 'string',
                Required: attr.required || false,
                MultiValued: attr.multiValued || false,
                Description: attr.description || '',
            }));
        },
        enabled: open && !!fullEntity && isSetEntity && !!filterScopeString,
    });

    const schemaAttributes =
        open && !!fullEntity && isSetEntity && !!filterScopeString ? (schemaQuery.data ?? []) : [];
    const schemaLoading =
        open && !!fullEntity && isSetEntity && !!filterScopeString && schemaQuery.isFetching;

    const isSetTransition = entityType?.toLowerCase() === 'settransition';
    const actionWorkflowValue = fullEntity?.ActionWorkflow || fullEntity?.actionWorkflow;
    const actionWorkflowId = actionWorkflowValue ? extractReferenceId(actionWorkflowValue) : null;

    const actionWorkflowQuery = useQuery({
        queryKey: ['entity', 'workflow', actionWorkflowId],
        queryFn: () => entityService.getEntityById(actionWorkflowId!, 'workflow'),
        enabled: open && !!fullEntity && isSetTransition && !!actionWorkflowId,
        retry: 0,
    });

    const actionWorkflowError = useMemo(() => {
        if (!isSetTransition || !fullEntity || !actionWorkflowId) return null;
        if (actionWorkflowQuery.isLoading) return null;
        if (actionWorkflowQuery.error) {
            const statusCode = (actionWorkflowQuery.error as any)?.response?.status;
            if (statusCode === 404) return 'Workflow not found (may have been deleted)';
            return `Error validating workflow: ${(actionWorkflowQuery.error as Error)?.message || 'Unknown error'}`;
        }
        if (actionWorkflowQuery.isSuccess && !actionWorkflowQuery.data) {
            return 'Workflow not found (may have been deleted)';
        }
        return null;
    }, [
        isSetTransition,
        fullEntity,
        actionWorkflowId,
        actionWorkflowQuery.isLoading,
        actionWorkflowQuery.error,
        actionWorkflowQuery.isSuccess,
        actionWorkflowQuery.data,
    ]);

    const actionWorkflowValidating = actionWorkflowQuery.isFetching;

    useEffect(() => {
        if (!open || !fullEntity || !isSetTransition || typeof actionWorkflowValue !== 'string') return;
        if (!actionWorkflowId) return;

        const entityQueryKey = ['entity', entityType, entityId, requestAttributes, false] as const;

        if (actionWorkflowQuery.isSuccess && actionWorkflowQuery.data) {
            const workflow = actionWorkflowQuery.data;
            const enriched = {
                Id: actionWorkflowId,
                DisplayName: workflow.DisplayName || actionWorkflowId,
                EntityType: 'Workflow',
                $ref: workflow.$ref || `#/Workflow/${actionWorkflowId}`,
            };
            setAdditionalLoadedAttrs((prev) => ({ ...prev, ActionWorkflow: enriched }));
            queryClient.setQueryData(entityQueryKey, (old: any) =>
                old ? { ...old, ActionWorkflow: enriched } : old
            );
        } else if (actionWorkflowQuery.isError) {
            const statusCode = (actionWorkflowQuery.error as any)?.response?.status;
            if (statusCode === 404) {
                const enriched = {
                    Id: actionWorkflowId,
                    DisplayName: actionWorkflowId,
                    EntityType: 'Workflow',
                };
                setAdditionalLoadedAttrs((prev) => ({ ...prev, ActionWorkflow: enriched }));
                queryClient.setQueryData(entityQueryKey, (old: any) =>
                    old ? { ...old, ActionWorkflow: enriched } : old
                );
            }
        }
    }, [
        open,
        fullEntity,
        isSetTransition,
        actionWorkflowValue,
        actionWorkflowId,
        actionWorkflowQuery.isSuccess,
        actionWorkflowQuery.isError,
        actionWorkflowQuery.data,
        actionWorkflowQuery.error,
        entityType,
        entityId,
        requestAttributes,
        queryClient,
    ]);

    const handleReferenceClick = useCallback(
        async (reference: any) => {
            if (!reference) return;

            let refId: string | undefined;
            let refType: string | undefined;

            if (reference.$ref) {
                const { parseScimReferenceUrl } = await import('../utils/scimUrlUtils');
                const urlInfo = parseScimReferenceUrl(reference.$ref);
                if (urlInfo) {
                    refId = urlInfo.entityId;
                    refType = urlInfo.entityType;
                }
            }

            if (!refId || !refType) {
                refId = reference.Id || reference.id;
                refType = reference.Type || reference.type || reference.EntityType;
            }

            if (!refId || !refType) {
                console.warn('Cannot open reference: missing Id or Type', reference);
                return;
            }

            const generateRoute = async (): Promise<string | null> => {
                const normalizedType = refType!.toLowerCase();

                if (normalizedType === 'schemaattribute') {
                    return `#meta/schemaattribute/${refId}`;
                } else if (normalizedType === 'schemaentity') {
                    return `#meta/schemaentity/${refId}`;
                } else if (normalizedType === 'schemabinding') {
                    return `#meta/schemabinding/${refId}`;
                }

                if (normalizedType === 'group') {
                    return `#groups/${refId}`;
                } else if (normalizedType === 'class') {
                    return `#classes/${refId}`;
                }

                try {
                    const category = await categorizationService.getEntityCategory(normalizedType);
                    if (category.type === 'person') {
                        return `#Person/${normalizedType}/${refId}`;
                    } else if (category.type === 'locality') {
                        return `#localities/${normalizedType}/${refId}`;
                    } else if (category.type === 'admin') {
                        return `#admin/${normalizedType}/${refId}`;
                    } else {
                        return `#other/${normalizedType}/${refId}`;
                    }
                } catch (routeError) {
                    console.error('Error generating route for reference:', routeError);
                    if (
                        normalizedType === 'set' ||
                        normalizedType === 'policyrule' ||
                        normalizedType === 'journalentry' ||
                        normalizedType === 'modificationerror'
                    ) {
                        return `#admin/${normalizedType}/${refId}`;
                    }
                    return `#other/${normalizedType}/${refId}`;
                }
            };

            const route = await generateRoute();
            if (route) {
                const newUrl = window.location.pathname + window.location.search + route;
                window.history.replaceState(null, '', newUrl);
                window.dispatchEvent(new HashChangeEvent('hashchange'));
            }

            setReferenceLoading(true);
            setReferenceDialogOpen(true);
            setReferenceEntityType(refType);

            try {
                if (refType?.toLowerCase() === 'schemabinding' && refId) {
                    console.warn(
                        `🚫 useEntityDetailLoader: Preventing incorrect API call for schema binding Entity field: ${refId} (should be schemaentity, not schemabinding)`
                    );
                    setReferenceEntity(null);
                    setReferenceLoading(false);
                    return;
                }

                const refEntity = await entityService.getEntityById(refId, refType, true);
                setReferenceEntity(refEntity);
            } catch (refError) {
                console.error(
                    `Selector data item fetch failed for referenced entity (entityType "${refType}", id "${refId}"):`,
                    refError
                );
                setReferenceEntity(null);
            } finally {
                setReferenceLoading(false);
            }
        },
        [entityService, categorizationService]
    );

    const closeReferenceDialog = useCallback(() => {
        setReferenceDialogOpen(false);
        setReferenceEntity(null);
        setReferenceEntityType('');
    }, []);

    const loadNonIndexedAttribute = useCallback(
        async (attributeKey: string): Promise<any | null> => {
            if (!entityId || !entityType) return null;
            setLoadingAttribute(attributeKey);
            try {
                const attributeForRequest =
                    (attributeKey ?? '').trim().replace(/^\$\./, '') || attributeKey;
                const result = await entityService.getEntityById(entityId, entityType, false, [
                    attributeForRequest,
                ]);
                if (result && typeof result === 'object') {
                    const trimmedKey = (attributeKey ?? '').trim();
                    const candidateKeys = [attributeKey, trimmedKey, attributeForRequest].filter(Boolean);
                    const lowerCandidates = new Set(candidateKeys.map((k) => k.toLowerCase()));

                    const directKey = candidateKeys.find((k) =>
                        Object.prototype.hasOwnProperty.call(result, k)
                    );
                    const resolvedKey =
                        directKey ??
                        Object.keys(result).find((k) => lowerCandidates.has((k ?? '').toLowerCase()));
                    const loadedValue = resolvedKey
                        ? (result as Record<string, unknown>)[resolvedKey]
                        : null;

                    const existingKey = fullEntity
                        ? Object.keys(fullEntity).find((k) => lowerCandidates.has((k ?? '').toLowerCase()))
                        : undefined;
                    const mergeKey = existingKey ?? attributeKey;

                    setAdditionalLoadedAttrs((prev) => ({ ...prev, [mergeKey]: loadedValue }));

                    queryClient.setQueryData(
                        ['entity', entityType, entityId, requestAttributes, false],
                        (old: any) => (old ? { ...old, [mergeKey]: loadedValue } : old)
                    );

                    const merged = fullEntity ? { ...fullEntity, [mergeKey]: loadedValue } : null;
                    return merged ? normalizeEntityId(merged) : null;
                }
                return null;
            } catch (err) {
                console.error('Failed to load non-indexed attribute', attributeKey, err);
                return null;
            } finally {
                setLoadingAttribute(null);
            }
        },
        [entityId, entityType, fullEntity, requestAttributes, queryClient]
    );

    useEffect(() => {
        if (!open) return;
        setSchemaEntityBindings(null);
        setSchemaEntitySchemaLoading(false);
    }, [open]);

    const possessiveTargetRawValue = useMemo(() => {
        if (!fullEntity) return undefined;
        const raw = fullEntity.PossessiveTargetAttribute
            || fullEntity.possessiveTargetAttribute
            || fullEntity.possessivetargetattribute;
        if (!raw) return undefined;
        if (typeof raw === 'string') return raw;
        if (typeof raw === 'object' && raw !== null) return extractReferenceDisplayName(raw) || String(raw);
        return String(raw);
    }, [fullEntity?.PossessiveTargetAttribute, fullEntity?.possessiveTargetAttribute, fullEntity?.possessivetargetattribute]);

    useEffect(() => {
        const isApprovalType = entityType?.toLowerCase() === 'requestapprovaltype';
        if (!open || !isApprovalType || !possessiveTargetRawValue) {
            setPossessiveTargetAttributeInfo(null);
            return;
        }

        let cancelled = false;

        const loadPossessiveTargetAttributeInfo = async () => {
            const cleanAttributeName = (possessiveTargetRawValue ?? '').toString().trim();
            if (!cleanAttributeName) {
                setPossessiveTargetAttributeInfo(null);
                return;
            }

            try {
                const schemaService = new ScimSchemaService();
                const response = await schemaService.getSchemaAttributes();
                const attributes = response.Resources || [];

                const attribute = attributes.find((attr: any) => {
                    const attrDisplayName = attr.DisplayName || '';
                    const attrId = (attr.Id || '').toString().trim();
                    return (
                        attrDisplayName === cleanAttributeName ||
                        attrId === cleanAttributeName ||
                        attrDisplayName.toLowerCase() === cleanAttributeName.toLowerCase()
                    );
                });

                if (cancelled) return;

                if (attribute) {
                    const entityNames =
                        attribute.Entities && attribute.Entities.length > 0
                            ? attribute.Entities.map((ent: any) => ent.DisplayName || ent.Id || ent).filter(
                                  Boolean
                              )
                            : [];

                    let isReferenceTo: string[] = [];
                    if (attribute.IsReferenceTo && Array.isArray(attribute.IsReferenceTo)) {
                        isReferenceTo = attribute.IsReferenceTo.map(
                            (ref: any) =>
                                extractReferenceId(ref) || extractReferenceDisplayName(ref) || String(ref)
                        ).filter(Boolean);
                    } else {
                        try {
                            const fullAttribute = await schemaService.getSchemaAttributeById(cleanAttributeName);
                            if (!cancelled && fullAttribute?.IsReferenceTo && Array.isArray(fullAttribute.IsReferenceTo)) {
                                isReferenceTo = fullAttribute.IsReferenceTo.map(
                                    (ref: any) =>
                                        extractReferenceId(ref) ||
                                        extractReferenceDisplayName(ref) ||
                                        String(ref)
                                ).filter(Boolean);
                            }
                        } catch (fetchErr) {
                            console.warn('Could not fetch full attribute for IsReferenceTo:', fetchErr);
                        }
                    }

                    if (!cancelled) {
                        if (entityNames.length > 0 || isReferenceTo.length > 0) {
                            setPossessiveTargetAttributeInfo({ entities: entityNames, isReferenceTo });
                        } else {
                            setPossessiveTargetAttributeInfo(null);
                        }
                    }
                } else {
                    if (!cancelled) setPossessiveTargetAttributeInfo(null);
                }
            } catch (err) {
                console.error('Error loading PossessiveTargetAttribute info:', err);
                if (!cancelled) setPossessiveTargetAttributeInfo(null);
            }
        };

        loadPossessiveTargetAttributeInfo();
        return () => { cancelled = true; };
    }, [open, possessiveTargetRawValue, entityType]);

    return {
        fullEntity,
        isLoading,
        error,
        photo: {
            photoData,
            photoLoading,
            shouldShowPhoto,
            photoExpanded,
            setPhotoExpanded,
        },
        schema: {
            schemaAttributes,
            schemaLoading,
        },
        deleteState: {
            isDeleting,
            deleteError,
            deleteDialogOpen,
            setIsDeleting,
            setDeleteError,
            setDeleteDialogOpen,
        },
        reference: {
            referenceDialogOpen,
            referenceEntity,
            referenceEntityType,
            referenceLoading,
            handleReferenceClick,
            closeReferenceDialog,
        },
        actionWorkflowError,
        actionWorkflowValidating,
        possessiveTargetAttributeInfo,
        schemaEntityBindings,
        schemaEntitySchemaLoading,
        targetEntitlement,
        targetEntitlementError,
        loadNonIndexedAttribute,
        loadingAttribute,
        entityService,
    };
}
